import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Container from "../../../../components/commonContainer/Container";
import companyApiService from "../../../services/companyApiService";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HistoryIcon from "@mui/icons-material/History";
import { showErrorToast } from "../../../../utils/toast/toast";
import PaymentHistoryModal from "./PaymentHistoryModal";
import "./Subscriptions.scss";

interface Company {
  id: number;
  companyName: string;
}

interface Plan {
  id: number;
  name: string;
  billingType: "monthly" | "yearly";
  amount: number;
  currency: string;
}

interface Subscription {
  id: number;
  companyId: number;
  planId: number;
  startDate: string;
  endDate: string;
  status: "pending" | "active" | "expired" | "cancelled";
  autoRenew: boolean;
  Company: Company;
  Plan: Plan;
}

const Subscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>("all");
  const [selectedCompany, setSelectedCompany] = useState<{
    id: number;
    name: string;
  } | null>(null);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await companyApiService.getAllSubscriptions();
      setSubscriptions(response.data.subscriptions || []);
    } catch (err) {
      console.error("Failed to fetch subscriptions:", err);
      showErrorToast("Failed to fetch subscriptions");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "status-active";
      case "expired":
        return "status-expired";
      case "cancelled":
        return "status-cancelled";
      default:
        return "status-pending";
    }
  };

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (filter === "all") return true;
    return sub.status === filter;
  });

  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter((s) => s.status === "active").length,
    expired: subscriptions.filter((s) => s.status === "expired").length,
    pending: subscriptions.filter((s) => s.status === "pending").length,
  };

  return (
    <Container>
      <div className="subscriptions-container">
        <div className="header-section">
          <h2>All Subscriptions</h2>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card total">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Subscriptions</div>
          </div>
          <div className="stat-card active">
            <div className="stat-number">{stats.active}</div>
            <div className="stat-label">Active</div>
          </div>
          <div className="stat-card expired">
            <div className="stat-number">{stats.expired}</div>
            <div className="stat-label">Expired</div>
          </div>
          <div className="stat-card pending">
            <div className="stat-number">{stats.pending}</div>
            <div className="stat-label">Pending</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            className={filter === "active" ? "active" : ""}
            onClick={() => setFilter("active")}
          >
            Active
          </button>
          <button
            className={filter === "expired" ? "active" : ""}
            onClick={() => setFilter("expired")}
          >
            Expired
          </button>
          <button
            className={filter === "pending" ? "active" : ""}
            onClick={() => setFilter("pending")}
          >
            Pending
          </button>
          <button
            className={filter === "cancelled" ? "active" : ""}
            onClick={() => setFilter("cancelled")}
          >
            Cancelled
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading subscriptions...</div>
        ) : (
          <div className="subscriptions-table">
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Billing</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Auto Renew</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty-row">
                      No subscriptions found
                    </td>
                  </tr>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <tr
                      key={subscription.id}
                      className="subscription-row"
                      onClick={() =>
                        setSelectedCompany({
                          id: subscription.companyId,
                          name: subscription.Company.companyName,
                        })
                      }
                    >
                      <td className="company-cell">
                        {subscription.Company.companyName}
                      </td>
                      <td className="plan-cell">{subscription.Plan.name}</td>
                      <td className="amount-cell">
                        {subscription.Plan.currency} {subscription.Plan.amount}
                      </td>
                      <td>{subscription.Plan.billingType}</td>
                      <td>{formatDate(subscription.startDate)}</td>
                      <td>{formatDate(subscription.endDate)}</td>
                      <td className="renew-cell">
                        {subscription.autoRenew ? (
                          <CheckCircleIcon className="yes-icon" />
                        ) : (
                          <CancelIcon className="no-icon" />
                        )}
                      </td>
                      <td>
                        <span
                          className={`status-badge ${getStatusColor(subscription.status)}`}
                        >
                          {subscription.status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="history-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompany({
                                id: subscription.companyId,
                                name: subscription.Company.companyName,
                              });
                            }}
                            title="Payment History"
                          >
                            <HistoryIcon />
                          </button>
                          <button
                            className="view-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/company/shops/${subscription.companyId}`,
                              );
                            }}
                            title="View Details"
                          >
                            <VisibilityIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Payment History Modal */}
        {selectedCompany && (
          <PaymentHistoryModal
            companyId={selectedCompany.id}
            companyName={selectedCompany.name}
            onClose={() => setSelectedCompany(null)}
          />
        )}
      </div>
    </Container>
  );
};

export default Subscriptions;
